// Условия списка фоновых задач в адресе страницы (002: contracts/api.md, GET /api/ops/jobs;
// FR-047): номер операции, тип, состояние, сортировка по времени создания. По умолчанию — все
// задачи, новые сверху. Номер операции ищется целиком: дефисы, строчные и буква O вместо нуля —
// как у сервера (normalizeOperation).
import {
  OPERATION_PATTERN,
  jobSortFields,
  jobStates,
  jobTypes,
  normalizeOperation,
} from '@kp/contracts';
import type { JobState, JobType, SortValue } from '@kp/contracts';
import type { BadgeTone } from '@kp/ui';

export type JobSort = SortValue<(typeof jobSortFields)[number]>;

export interface JobsSearch {
  /** Номер операции, как его ввели: «7F3K-92QD-1A2B». */
  q?: string;
  type?: JobType;
  state?: JobState;
  sort?: JobSort;
}

export const DEFAULT_JOBS_SORT: JobSort = '-createdAt';

const sortValues: readonly string[] = jobSortFields.flatMap((field) => [field, `-${field}`]);

/** Для адреса страницы: validateSearch маршрута /ops/jobs. */
export function parseJobsSearch(search: Record<string, unknown>): JobsSearch {
  const result: JobsSearch = {};
  if (typeof search.q === 'string' && search.q.trim() !== '') result.q = search.q.trim();
  if (typeof search.type === 'string') {
    const type = jobTypes.find((known) => known === search.type);
    if (type) result.type = type;
  }
  if (typeof search.state === 'string') {
    const state = jobStates.find((known) => known === search.state);
    if (state) result.state = state;
  }
  if (typeof search.sort === 'string' && sortValues.includes(search.sort)) {
    result.sort = search.sort as JobSort;
  }
  return result;
}

/**
 * Номер операции для запроса: 12 знаков Base32 Крокфорда после нормализации; неполный номер
 * не ищется — undefined.
 */
export function operationQuery(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const normalized = normalizeOperation(text.trim());
  return OPERATION_PATTERN.test(normalized) ? normalized : undefined;
}

/** Условия сужают список: «Нет задач по этим условиям» вместо «Задач пока нет». */
export function hasJobFilters(search: JobsSearch): boolean {
  return Boolean(operationQuery(search.q) || search.type || search.state);
}

/** Цвет состояния; слово — jobStateLabels из @kp/contracts. */
export const jobStateTones = {
  queued: 'neutral',
  running: 'accent',
  done: 'ok',
  failed: 'bad',
  cancelled: 'neutral',
} as const satisfies Record<JobState, BadgeTone>;
